import { useState } from "react";
import { useAppStore } from "@/store";
import {
  SidebarSection,
  SidebarSectionEmpty,
  SidebarSectionHeader,
  SidebarSectionHeaderSlotName,
  SidebarSectionItem,
  SidebarSectionItemSlotName,
  SidebarSectionSlotName,
} from "@/components/layout/components/sidebar/sidebar-section";
import { observer } from "mobx-react-lite";
import { NoteObservable } from "@/store/note";
import { SectionListTitleWrapper, SeeAllButton } from "./sidebar-contents.style";
import {
  MDSButtonGroup,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
  MDSTooltip,
  MDSTooltipPlacement,
  MDSTextVariant,
  MDSText,
  MDSTextColor,
  MDSTextWeight,
  MDSIconKind,
} from "@/mds";
import { EventContext } from "@/domains/metrics/context";
import { motion } from "motion/react";

interface SidebarContentsRecentNotesListProps {
  onClick?: () => void;
  includeTitle?: boolean;
  indent?: boolean;
}

interface SidebarContentsRecentNotesProps {
  onClick?: () => void;
}

export const SidebarContentsRecentNotes = observer(function SidebarContentsRecentNotes({
  onClick,
}: SidebarContentsRecentNotesProps) {
  const { store } = useAppStore();
  const [isExpanded, setIsExpanded] = useState(true);

  const hasRecentNotes =
    store.recentItems.sortedRecentItems.filter(item => item instanceof NoteObservable).length > 0;

  return (
    <SidebarSection isExpanded={isExpanded}>
      <SidebarSectionHeader
        slot={SidebarSectionSlotName.Header}
        label="Notes"
        defaultIconKind={MDSIconKind.Notebook}
        isExpanded={isExpanded}
        onPress={() => setIsExpanded(!isExpanded)}
      >
        <MDSButtonGroup slot={SidebarSectionHeaderSlotName.ButtonsRight}>
          <MDSTooltip config={{ label: "Create note", placement: MDSTooltipPlacement.Top }}>
            <MDSIconButton
              size={MDSIconButtonSize.XSmall}
              iconKind={MDSIconKind.Plus}
              variant={MDSIconButtonVariant.OnChrome}
              onClick={() =>
                store.notes.composeNewNote({ eventContext: EventContext.GlobalActions })
              }
              rounded
            />
          </MDSTooltip>
        </MDSButtonGroup>
      </SidebarSectionHeader>
      {hasRecentNotes && (
        <>
          <SidebarContentsRecentNotesList onClick={onClick} indent />
          <SeeAllButton
            onClick={() => {
              onClick?.();
              store.navigation.goToNotes();
            }}
          >
            See All
          </SeeAllButton>
        </>
      )}
      {!hasRecentNotes && (
        <SidebarSectionEmpty>
          <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Tertiary}>
            Recent notes appear here
          </MDSText>
        </SidebarSectionEmpty>
      )}
    </SidebarSection>
  );
});

export const SidebarContentsRecentNotesList = observer(function SidebarContentsRecentNotesList({
  onClick,
  includeTitle,
  indent,
}: SidebarContentsRecentNotesListProps) {
  const { store } = useAppStore();
  const recentNotes = store.recentItems.sortedRecentItems
    .filter(item => item instanceof NoteObservable)
    .slice(0, 5);

  return (
    <>
      {includeTitle && (
        <SectionListTitleWrapper>
          <MDSText
            variant={MDSTextVariant.TextXs}
            weight={MDSTextWeight.SemiBold}
            color={MDSTextColor.Secondary}
          >
            Recent Notes
          </MDSText>
        </SectionListTitleWrapper>
      )}
      {recentNotes.map(note => (
        <motion.div key={note.id} layoutId={note.id}>
          <SidebarSectionItem
            key={note.id}
            label={note.suggestionData?.suggested_primary_label || note.title}
            iconKind={MDSIconKind.Document}
            onClick={() => {
              onClick?.();
              store.navigation.goToNote({ noteId: note.id });
            }}
            indent={indent}
          >
            <MDSButtonGroup slot={SidebarSectionItemSlotName.ButtonsRight}>
              <MDSTooltip
                config={{
                  label: note.isFavorited ? "Unpin from sidebar" : "Pin to sidebar",
                  placement: MDSTooltipPlacement.Top,
                }}
              >
                <MDSIconButton
                  size={MDSIconButtonSize.XSmall}
                  iconKind={note.isFavorited ? MDSIconKind.ThumbtackSolid : MDSIconKind.Thumbtack}
                  variant={MDSIconButtonVariant.OnChrome}
                  onClick={() => {
                    onClick?.();
                    note.toggleFavorite();
                  }}
                  rounded
                />
              </MDSTooltip>
            </MDSButtonGroup>
          </SidebarSectionItem>
        </motion.div>
      ))}
    </>
  );
});
